import { cva, type VariantProps } from 'class-variance-authority'
import classNames from 'classnames'
import { Checkmark } from 'grommet-icons'
import React, { ButtonHTMLAttributes, ReactNode } from 'react'
import { IoAddSharp } from 'react-icons/io5'
import { Link, LinkProps } from 'react-router-dom'
import { PencilIcon } from '../../../assets/svgs'
import { palette } from '../../../themes/palette'
import styles from './button.module.css'

const buttonStyle = cva(
  'flex items-center justify-center rounded-md gap-2.5 text-[16px] font-normal gap-2 leading-6 cursor-pointer',
  {
    defaultVariants: {
      block: false,
      kinds: 'primary',
      rounded: true,
      size: 'lg'
    },
    variants: {
      block: { true: '!w-full' },
      kinds: {
        normal: 'border-none hover:opacity-70 text-black',
        outline: 'border-[#E6E6E6] border text-[#5C6C71] hover:opacity-70 bg-transparent hover:text-blue-300',
        primary: 'bg-primary hover:opacity-80 text-white',
        secondary: 'bg-[#F5F5F5] hover:text-black text-black rounded-lg hover:text-primary ease-in-out duration-200',
        warning: 'bg-[#FF0000] hover:opacity-70 text-white'
      },
      rounded: {
        true: '!rounded-full'
      },
      size: {
        auto: 'h-auto',
        lg: 'px-4 py-3',
        md: 'px-3 py-2',
        sm: 'px-2.5 py-1',
        wide: 'px-6 py-3'
      }
    }
  }
)

type ButtonBaseProps = VariantProps<typeof buttonStyle> & {
  children?: ReactNode
  className?: string
  disabled?: boolean
  isLoading?: boolean
  icon?: React.ReactElement
  label?: string // Backward compatibility
  reverse?: boolean
  style?: React.CSSProperties
  textStyle?: React.CSSProperties
}

type ButtonProps = ButtonBaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    to?: never
  }

type ButtonLinkProps = ButtonBaseProps &
  LinkProps & {
    to: string
  }

// Backward compatibility interface
export interface IButtonProps {
  label?: string
  style?: React.CSSProperties
  textStyle?: React.CSSProperties
  onClick?: (x: unknown) => void
  isLoading?: boolean
  icon?: React.ReactElement
  children?: React.ReactNode
  to?: string
  disabled?: boolean
  className?: string
  plain?: boolean
  reverse?: boolean
}

export function Button(props: ButtonProps | ButtonLinkProps | IButtonProps) {
  const {
    className,
    disabled,
    to,
    children,
    isLoading,
    block,
    kinds,
    rounded,
    size,
    icon,
    label,
    primary,
    secondary,
    plain,
    style,
    textStyle,
    onClick,
    reverse,
    ...rest
  } = props as any

  // Determine rounded based on prop

  // Determine spinner color based on button kind
  // const spinnerColor = kinds === 'primary' || kinds === 'warning' ? 'border-t-white' : 'border-t-primary'

  const inner = (
    <>
      {isLoading && (
        <div
          className={`h-4 w-4 animate-spin rounded-full border-2 border-t-white border-r-transparent border-b-transparent border-l-transparent`}
          data-testid="spinner"
        />
      )}
      <>
        {!reverse && icon && <span className="flex items-center">{icon}</span>}
        {children ? children : <span style={textStyle}>{label || 'Submit'}</span>}
        {reverse && icon && <span className="flex items-center">{icon}</span>}
      </>
    </>
  )

  const buttonClassName = classNames(
    buttonStyle({ block, kinds, rounded, size }),
    {
      '!cursor-not-allowed bg-opacity-50': disabled === true
    },
    className
  )

  // Merge inline styles
  const mergedStyle = {
    ...(style || {})
  }

  if (to) {
    return (
      <Link
        className={buttonClassName}
        onClick={disabled ? e => e.preventDefault() : onClick}
        style={mergedStyle}
        to={to}
      >
        {inner}
      </Link>
    )
  }

  return (
    <button
      className={buttonClassName}
      disabled={disabled || isLoading}
      onClick={onClick}
      style={mergedStyle}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {inner}
    </button>
  )
}

interface IconButtonProps {
  label: string
  labelColor?: string
  onClick?: () => void
  disabled?: boolean
  className?: string
  icon?: React.ReactElement // For backward compatibility
}

export const IconButton = ({ label, labelColor, onClick, disabled, className, icon }: IconButtonProps) => {
  return (
    <button
      className={`flex items-center gap-1 ${className || ''}`}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      <div className="w-6 h-6 rounded-full bg-[#81A16F] flex items-center justify-center">
        {icon || <IoAddSharp size={18} />}
      </div>
      <span className="text-sm" style={{ color: labelColor || '#000000' }}>
        {label}
      </span>
    </button>
  )
}

export const EditButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      className="w-8 h-8 rounded-full bg-[white] flex items-center justify-center p-2 hover:opacity-70 transition-opacity"
      onClick={onClick}
      type="button"
    >
      <PencilIcon />
    </button>
  )
}

type Props = {
  id: string
  isToggledOn: boolean
  onToggle: (id: string, state: boolean) => void
  className?: string
  disabled: boolean
}

export const CheckButton = ({ label, onClick }: { label: string; onClick?: () => void }) => {
  return (
    <Button
      className="rounded-full"
      icon={<Checkmark color="white" size="14px" />}
      label={label}
      onClick={onClick}
      style={{
        backgroundColor: palette.royalBrown,
        color: 'white',
        fontSize: '14px',
        fontWeight: 400,
        padding: '10px 20px'
      }}
    />
  )
}

export const ToggleBtn = ({ id, onToggle, className, isToggledOn, disabled = false }: Props) => {
  return (
    <div
      className={`${styles.toggleTransition} ${isToggledOn ? 'bg-[#141B34]' : 'bg-[#BEBEC2]'} cursor-pointer bg-toggle w-6 flex rounded-[10px] items-center p-[2px] ${className || ''}`}
      onClick={() => {
        if (!disabled) {
          onToggle(id, !isToggledOn)
        }
      }}
      style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
    >
      <div
        className={`${styles.toggleTransition} ${!isToggledOn ? '' : 'ml-[8.9px]'} w-[10px] h-[10px] rounded-full bg-slate-50`}
      ></div>
    </div>
  )
}
